import React from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMessageCircle } from 'react-icons/fi';

export default function Hero() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' },
    },
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center pt-20 pb-20 bg-gradient-to-b from-white to-gray-50 overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 rounded-full bg-gray-200 opacity-20 blur-3xl"
          animate={{ y: [0, 50, 0] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-gray-200 opacity-20 blur-3xl"
          animate={{ y: [0, -50, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Superheading */}
          <motion.div variants={itemVariants} className="mb-6">
            <span className="inline-block px-4 py-2 bg-black text-white rounded-full text-sm font-semibold">
              Premium Construction & Design
            </span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            variants={itemVariants}
            className="text-5xl sm:text-6xl lg:text-5xl font-bold text-black leading-tight mb-6"
          >
            We Build your Dream Space
            <br />
            <motion.span
              className="text-black relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              in Chennai
            </motion.span>
          </motion.h1>

          {/* Subheading */}
          <motion.p
            variants={itemVariants}
            className="text-sm sm:text-2xl text-gray-600 max-w-3xl mx-auto mb-8"
          >
            Sankar Construction is a leading construction company in Chennai with over 15 years
            of experience building homes, villas, and commercial spaces. We serve clients
            across Chennai including OMR, ECR, Tambaram, Velachery, Porur, Ambattur, Avadi,
            and surrounding districts like Kanchipuram and Chengalpattu.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <motion.a
              href="tel:9841113286"
              className="inline-flex items-center justify-center gap-2 px-8 py-4  text-white font-semibold rounded-lg bg-blue-800 transition-all"
              whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(0,0,0,0.2)' }}
              whileTap={{ scale: 0.95 }}
            >
              <FiPhone size={20} />
              Call Now: 9841113286
            </motion.a>
            <motion.a
              href="https://wa.me/919150187138"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2  font-semibold rounded-lg bg-green-600 text-white transition-all"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiMessageCircle size={20} />
              WhatsApp: 9150187138
            </motion.a>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            className="flex justify-center pt-8"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="flex flex-col items-center gap-2 text-gray-600">
              <p className="text-sm font-medium">Scroll to explore</p>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* SEO Content - Hidden from users but visible to search engines */}
      <div className="sr-only">
        <h1>Trusted Construction Company in Chennai</h1>
        <h2>Residential & Commercial Construction Services Across Chennai</h2>
        <p>Sankar Construction is a leading construction company in Chennai with over 15 years of experience building homes, villas, and commercial spaces. We serve clients across Chennai including OMR, ECR, Tambaram, Velachery, Porur, Ambattur, Avadi, and surrounding districts like Kanchipuram and Chengalpattu.</p>

        {/* High Priority Chennai City Keywords */}
        <h3>Construction Company in Chennai</h3>
        <p>Best construction company in Chennai, building contractors in Chennai, home construction in Chennai, residential construction Chennai, commercial construction Chennai, house building contractors Chennai, construction services Chennai.</p>

        {/* Surrounding Areas Keywords */}
        <h3>Construction Services in Chennai Surrounding Areas</h3>
        <p>Construction company in Tambaram, builders in Velachery, construction in OMR Chennai, builders in Porur, construction company Ambattur, home builders in Avadi, contractors in Poonamallee, construction in Sholinganallur, builders near ECR Chennai, construction company Chromepet, contractors in Pallavaram, builders in Perungudi, construction in Siruseri, builders Guduvanchery, construction company Sriperumbudur, contractors in Kanchipuram near Chennai, builders in Mahabalipuram road.</p>

        {/* Long-tail Keywords */}
        <h3>Specialized Construction Services in Chennai</h3>
        <p>Affordable house construction in Chennai, independent house construction cost Chennai, villa construction company Chennai, low cost construction in Chennai, 2BHK house construction Chennai, 3BHK home builders Chennai, residential plot construction Chennai, turnkey construction company Chennai, G+1 G+2 building contractors Chennai, commercial building construction Chennai.</p>

        <h3>Our Construction Services</h3>
        <ul>
          <li>Residential House Construction in Chennai - Custom homes, villas, apartments</li>
          <li>Commercial Building Construction Chennai - Office buildings, retail spaces, warehouses</li>
          <li>Villa & Duplex Construction Chennai - Luxury villa construction, duplex homes</li>
          <li>Renovation & Remodeling Services Chennai - Complete home and office renovations</li>
          <li>Interior Design & Finishing Chennai - Kitchen remodeling, bathroom design</li>
          <li>Turnkey Construction Projects Chennai - Complete construction solutions</li>
        </ul>

        <h3>About Sankar Construction</h3>
        <p>Sankar Construction is Chennai's trusted builders since 15 years, offering quality construction services across Tamil Nadu. Our experienced team specializes in residential and commercial construction projects.</p>

        <h3>Contact Information</h3>
        <p>Phone: 9841113286 | WhatsApp: 9150187138 | Office: Ayanavaram, Chennai - 600023 | Email: iyyappanramana122@gmail.com</p>

        <h3>Service Areas</h3>
        <p>Chennai, Tambaram, Velachery, OMR, ECR, Porur, Ambattur, Avadi, Chromepet, Sholinganallur, Perungudi, Siruseri, Guduvanchery, Sriperumbudur, Kanchipuram, Chengalpattu, Poonamallee, Pallavaram, Mahabalipuram road.</p>
      </div>
    </section>
  );
}
